/**
 * Service Request Modal
 * Integrated submission for Copyright Registration and Publishing Services
 */

import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { X, Upload, FileText, Check, Loader2, CreditCard } from 'lucide-react';
import { API_URL } from '../config';
import { initiateServicePayment, checkServicePayment } from '../utils/payment';

export function ServiceRequestModal({ 
  document, 
  serviceType, // 'copyright' or 'publishing'
  onClose, 
  onSuccess 
}) {
  const [formData, setFormData] = useState({
    service_type: serviceType,
    document_id: document?.id || '',
    document_title: document?.title || '',
    reason: '',
    additional_files: null,
    notes: '',
    contact_preference: 'email',
    // Copyright registration specific fields
    author_full_name: '',
    author_address: '',
    author_city: '',
    author_state: '',
    author_zip: '',
    author_country: 'United States',
    author_citizenship: '',
    work_title: document?.title || '',
    year_created: new Date().getFullYear(),
    year_published: '',
    publisher_name: '',
    registration_type: 'literary_work', // literary_work, visual_arts, performing_arts, etc.
    pseudonym: '',
    work_for_hire: false,
    previous_registration: false,
    previous_reg_number: '',
    // Amazon KDP Publishing specific fields
    book_title: document?.title || '',
    book_subtitle: '',
    series_name: '',
    series_number: '',
    author_bio: '',
    book_description: '',
    keywords: '',
    primary_category: '',
    secondary_category: '',
    language: 'English',
    print_isbn: '',
    ebook_format: 'both', // ebook, paperback, both
    paperback_size: '6x9',
    ebook_price: '2.99',
    paperback_price: '9.99',
    age_range: '',
    content_rating: 'general'
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [paymentChecking, setPaymentChecking] = useState(true);
  const [hasPaid, setHasPaid] = useState(false);

  const serviceInfo = {
    copyright: {
      title: 'Copyright Registration Service',
      price: '$99',
      includes: [
        'WGA Registry + U.S. Copyright Office registration',
        'Professional PDF preparation with watermarks',
        'Complete submission on your behalf',
        'Status tracking and confirmation',
        'Certificate copy when received (3-8 months)'
      ],
      description: 'We will register your work with both the WGA Registry and U.S. Copyright Office.'
    },
    publishing: {
      title: 'Amazon Publishing Service',
      price: '$299',
      includes: [
        'Professional eBook and paperback formatting',
        'Amazon KDP account setup and optimization',
        'Keyword research and category selection',
        'Complete upload to Amazon',
        'Launch strategy consultation'
      ],
      description: 'We will format and publish your book on Amazon KDP.'
    }
  };

  const info = serviceInfo[serviceType];

  // Check if user has already paid for this service
  useEffect(() => {
    async function checkPayment() {
      try {
        const result = await checkServicePayment(serviceType);
        setHasPaid(result.paid);
      } catch (err) {
        console.error('Error checking payment:', err);
      } finally {
        setPaymentChecking(false);
      }
    }
    checkPayment();
  }, [serviceType]);

  const handleProceedToPayment = async () => {
    setSubmitting(true);
    setError('');

    try {
      // Save form data to sessionStorage before redirecting to Stripe
      sessionStorage.setItem('serviceFormData', JSON.stringify(formData));
      sessionStorage.setItem('serviceDocumentId', document?.id || '');
      
      // Initiate Stripe checkout
      const { url } = await initiateServicePayment(serviceType, {
        document_id: document?.id,
        document_title: document?.title
      });

      // Redirect to Stripe
      window.location.href = url;
    } catch (err) {
      setError(err.message || 'Failed to initiate payment');
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('service_type', formData.service_type);
      submitData.append('document_id', formData.document_id);
      submitData.append('document_title', formData.document_title);
      submitData.append('reason', formData.reason);
      submitData.append('notes', formData.notes);
      submitData.append('contact_preference', formData.contact_preference);
      
      // Copyright registration specific fields
      if (formData.service_type === 'copyright') {
        submitData.append('author_full_name', formData.author_full_name);
        submitData.append('author_address', formData.author_address);
        submitData.append('author_city', formData.author_city);
        submitData.append('author_state', formData.author_state);
        submitData.append('author_zip', formData.author_zip);
        submitData.append('author_country', formData.author_country);
        submitData.append('author_citizenship', formData.author_citizenship);
        submitData.append('work_title', formData.work_title);
        submitData.append('year_created', formData.year_created.toString());
        submitData.append('year_published', formData.year_published);
        submitData.append('publisher_name', formData.publisher_name);
        submitData.append('registration_type', formData.registration_type);
        submitData.append('pseudonym', formData.pseudonym);
        submitData.append('work_for_hire', formData.work_for_hire.toString());
        submitData.append('previous_registration', formData.previous_registration.toString());
        submitData.append('previous_reg_number', formData.previous_reg_number);
      }
      
      // Amazon KDP Publishing specific fields
      if (formData.service_type === 'publishing') {
        submitData.append('book_title', formData.book_title);
        submitData.append('book_subtitle', formData.book_subtitle);
        submitData.append('series_name', formData.series_name);
        submitData.append('series_number', formData.series_number);
        submitData.append('author_bio', formData.author_bio);
        submitData.append('book_description', formData.book_description);
        submitData.append('keywords', formData.keywords);
        submitData.append('primary_category', formData.primary_category);
        submitData.append('secondary_category', formData.secondary_category);
        submitData.append('language', formData.language);
        submitData.append('print_isbn', formData.print_isbn);
        submitData.append('ebook_format', formData.ebook_format);
        submitData.append('paperback_size', formData.paperback_size);
        submitData.append('ebook_price', formData.ebook_price);
        submitData.append('paperback_price', formData.paperback_price);
        submitData.append('age_range', formData.age_range);
        submitData.append('content_rating', formData.content_rating);
      }
      
      if (formData.additional_files) {
        submitData.append('additional_files', formData.additional_files);
      }

      const response = await fetch(`${API_URL}/api/services/request`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Submission failed');
      }

      setSuccess(true);
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);

    } catch (err) {
      setError(err.message || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold mb-2">
              {serviceType === 'copyright' ? 'Copyright Submission Received!' : 'Publishing Request Submitted!'}
            </h3>
            <p className="text-gray-600">
              We will review your {serviceType === 'copyright' ? 'copyright registration details' : 'publishing request'} and contact you within 24-48 hours to discuss next steps and payment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <Card className="w-full max-w-3xl my-8 max-h-[90vh] flex flex-col">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">{info.title}</CardTitle>
              <CardDescription className="mt-1">
                {serviceType === 'copyright' 
                  ? 'Submit your work for professional copyright registration' 
                  : 'Submit your manuscript for professional Amazon publishing'}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-6 overflow-y-auto">
          {/* Service Info */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-lg">What&rsquo;s Included</h4>
              <div className="text-2xl font-bold text-purple-600">{info.price}</div>
            </div>
            <ul className="space-y-2">
              {info.includes.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm">
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Document Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <FileText className="w-8 h-8 text-purple-600" />
                <div className="flex-1">
                  <div className="font-semibold">{document?.title || 'Your Document'}</div>
                  <div className="text-sm text-gray-600">
                    {document?.doc_type || 'Document'} • {document?.word_count?.toLocaleString() || 0} words
                  </div>
                </div>
              </div>
            </div>

            {/* Copyright Registration Form Fields */}
            {serviceType === 'copyright' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-blue-900">
                    Copyright Registration Information
                  </h3>
                </div>
                <p className="text-sm text-blue-800">
                  This information is required for U.S. Copyright Office Form CO submission. 
                  All fields marked with * are mandatory for registration.
                </p>

                {/* Author Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Author's Full Legal Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.author_full_name}
                      onChange={(e) => setFormData({...formData, author_full_name: e.target.value})}
                      placeholder="John David Smith"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">Must match legal identification</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Pseudonym/Pen Name (if applicable)
                    </label>
                    <input
                      type="text"
                      value={formData.pseudonym}
                      onChange={(e) => setFormData({...formData, pseudonym: e.target.value})}
                      placeholder="J.D. Smith"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Address Information */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Author's Address *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.author_address}
                    onChange={(e) => setFormData({...formData, author_address: e.target.value})}
                    placeholder="123 Main Street, Apt 4B"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 mb-3"
                  />
                  
                  <div className="grid md:grid-cols-4 gap-3">
                    <div className="md:col-span-2">
                      <input
                        type="text"
                        required
                        value={formData.author_city}
                        onChange={(e) => setFormData({...formData, author_city: e.target.value})}
                        placeholder="City"
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        required
                        value={formData.author_state}
                        onChange={(e) => setFormData({...formData, author_state: e.target.value})}
                        placeholder="State"
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        required
                        value={formData.author_zip}
                        onChange={(e) => setFormData({...formData, author_zip: e.target.value})}
                        placeholder="ZIP"
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Citizenship */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Country *
                    </label>
                    <select
                      required
                      value={formData.author_country}
                      onChange={(e) => setFormData({...formData, author_country: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="United States">United States</option>
                      <option value="Canada">Canada</option>
                      <option value="United Kingdom">United Kingdom</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Citizenship/Domicile *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.author_citizenship}
                      onChange={(e) => setFormData({...formData, author_citizenship: e.target.value})}
                      placeholder="United States"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Work Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Year Work was Created *
                    </label>
                    <input
                      type="number"
                      required
                      min="1900"
                      max={new Date().getFullYear()}
                      value={formData.year_created}
                      onChange={(e) => setFormData({...formData, year_created: parseInt(e.target.value)})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Year First Published (if published)
                    </label>
                    <input
                      type="number"
                      min="1900"
                      max={new Date().getFullYear()}
                      value={formData.year_published}
                      onChange={(e) => setFormData({...formData, year_published: e.target.value})}
                      placeholder="Leave blank if unpublished"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Publisher (if published) */}
                {formData.year_published && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Publisher Name
                    </label>
                    <input
                      type="text"
                      value={formData.publisher_name}
                      onChange={(e) => setFormData({...formData, publisher_name: e.target.value})}
                      placeholder="Name of publishing company"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}

                {/* Work Type */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Type of Work *
                  </label>
                  <select
                    required
                    value={formData.registration_type}
                    onChange={(e) => setFormData({...formData, registration_type: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="literary_work">Literary Work (novel, script, etc.)</option>
                    <option value="performing_arts">Performing Arts Work (screenplay, play)</option>
                    <option value="visual_arts">Visual Arts Work (photography, artwork)</option>
                    <option value="sound_recording">Sound Recording</option>
                  </select>
                </div>

                {/* Checkboxes */}
                <div className="space-y-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.work_for_hire}
                      onChange={(e) => setFormData({...formData, work_for_hire: e.target.checked})}
                      className="w-4 h-4 mt-1 text-blue-600"
                    />
                    <div>
                      <div className="font-medium">This is a work made for hire</div>
                      <p className="text-xs text-gray-600">Check only if you created this work as an employee or under contract</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.previous_registration}
                      onChange={(e) => setFormData({...formData, previous_registration: e.target.checked})}
                      className="w-4 h-4 mt-1 text-blue-600"
                    />
                    <div>
                      <div className="font-medium">This work has been previously registered</div>
                      <p className="text-xs text-gray-600">Check if there's an existing copyright registration for this work</p>
                    </div>
                  </label>
                </div>

                {formData.previous_registration && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Previous Registration Number
                    </label>
                    <input
                      type="text"
                      value={formData.previous_reg_number}
                      onChange={(e) => setFormData({...formData, previous_reg_number: e.target.value})}
                      placeholder="TX 1-234-567"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Amazon KDP Publishing Form Fields */}
            {serviceType === 'publishing' && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 space-y-6">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-semibold text-purple-900">
                    Amazon KDP Publishing Details
                  </h3>
                </div>
                <p className="text-sm text-purple-800">
                  This information will be used to create your Amazon KDP listing. 
                  All fields marked with * are required for publishing.
                </p>

                {/* Book Title & Subtitle */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Book Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.book_title}
                      onChange={(e) => setFormData({...formData, book_title: e.target.value})}
                      placeholder="The Amazing Journey"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Subtitle (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.book_subtitle}
                      onChange={(e) => setFormData({...formData, book_subtitle: e.target.value})}
                      placeholder="A Tale of Adventure"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Series Information */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Series Name (if part of series)
                    </label>
                    <input
                      type="text"
                      value={formData.series_name}
                      onChange={(e) => setFormData({...formData, series_name: e.target.value})}
                      placeholder="The Adventure Chronicles"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Book Number in Series
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.series_number}
                      onChange={(e) => setFormData({...formData, series_number: e.target.value})}
                      placeholder="1"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                  </div>
                </div>

                {/* Author Bio */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Author Bio *
                  </label>
                  <textarea
                    required
                    value={formData.author_bio}
                    onChange={(e) => setFormData({...formData, author_bio: e.target.value})}
                    rows={4}
                    placeholder="Write a compelling author biography (2-3 paragraphs recommended). Include your background, writing experience, and what makes you unique..."
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">This will appear on your Amazon author page</p>
                </div>

                {/* Book Description */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Book Description/Blurb *
                  </label>
                  <textarea
                    required
                    value={formData.book_description}
                    onChange={(e) => setFormData({...formData, book_description: e.target.value})}
                    rows={6}
                    placeholder="Write a compelling book description that will appear on Amazon. Hook readers with an engaging summary, key themes, and what makes your book special..."
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">This is your sales pitch - make it compelling!</p>
                </div>

                {/* Keywords */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Keywords *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.keywords}
                    onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                    placeholder="fantasy adventure, hero journey, epic quest, magic system"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                  <p className="text-xs text-gray-600 mt-1">Enter 5-7 keywords separated by commas. These help readers find your book.</p>
                </div>

                {/* Categories */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Primary Category *
                    </label>
                    <select
                      required
                      value={formData.primary_category}
                      onChange={(e) => setFormData({...formData, primary_category: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select category...</option>
                      <option value="fiction">Fiction</option>
                      <option value="mystery">Mystery & Thriller</option>
                      <option value="romance">Romance</option>
                      <option value="scifi">Science Fiction & Fantasy</option>
                      <option value="literary">Literary Fiction</option>
                      <option value="historical">Historical Fiction</option>
                      <option value="nonfiction">Non-Fiction</option>
                      <option value="biography">Biography & Memoir</option>
                      <option value="selfhelp">Self-Help</option>
                      <option value="business">Business & Economics</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Secondary Category (Optional)
                    </label>
                    <select
                      value={formData.secondary_category}
                      onChange={(e) => setFormData({...formData, secondary_category: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Select category...</option>
                      <option value="fiction">Fiction</option>
                      <option value="mystery">Mystery & Thriller</option>
                      <option value="romance">Romance</option>
                      <option value="scifi">Science Fiction & Fantasy</option>
                      <option value="literary">Literary Fiction</option>
                      <option value="historical">Historical Fiction</option>
                      <option value="young_adult">Young Adult</option>
                      <option value="childrens">Children's Books</option>
                    </select>
                  </div>
                </div>

                {/* Format & Pricing */}
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Publishing Format *
                  </label>
                  <select
                    required
                    value={formData.ebook_format}
                    onChange={(e) => setFormData({...formData, ebook_format: e.target.value})}
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="both">Both eBook & Paperback</option>
                    <option value="ebook">eBook Only</option>
                    <option value="paperback">Paperback Only</option>
                  </select>
                </div>

                {(formData.ebook_format === 'paperback' || formData.ebook_format === 'both') && (
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Paperback Size *
                    </label>
                    <select
                      required
                      value={formData.paperback_size}
                      onChange={(e) => setFormData({...formData, paperback_size: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="6x9">6" x 9" (Standard Novel)</option>
                      <option value="5x8">5" x 8" (Compact)</option>
                      <option value="5.5x8.5">5.5" x 8.5" (Digest)</option>
                      <option value="8x10">8" x 10" (Large Format)</option>
                    </select>
                  </div>
                )}

                {/* Pricing */}
                <div className="grid md:grid-cols-2 gap-4">
                  {(formData.ebook_format === 'ebook' || formData.ebook_format === 'both') && (
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        eBook Price (USD) *
                      </label>
                      <input
                        type="number"
                        required
                        min="0.99"
                        max="9.99"
                        step="0.01"
                        value={formData.ebook_price}
                        onChange={(e) => setFormData({...formData, ebook_price: e.target.value})}
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-600 mt-1">Recommended: $2.99 - $4.99 for 70% royalty</p>
                    </div>
                  )}

                  {(formData.ebook_format === 'paperback' || formData.ebook_format === 'both') && (
                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Paperback Price (USD) *
                      </label>
                      <input
                        type="number"
                        required
                        min="2.99"
                        step="0.01"
                        value={formData.paperback_price}
                        onChange={(e) => setFormData({...formData, paperback_price: e.target.value})}
                        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                      <p className="text-xs text-gray-600 mt-1">Will be adjusted based on page count and printing costs</p>
                    </div>
                  )}
                </div>

                {/* ISBN & Language */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Language *
                    </label>
                    <select
                      required
                      value={formData.language}
                      onChange={(e) => setFormData({...formData, language: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="English">English</option>
                      <option value="Spanish">Spanish</option>
                      <option value="French">French</option>
                      <option value="German">German</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Print ISBN (if you have one)
                    </label>
                    <input
                      type="text"
                      value={formData.print_isbn}
                      onChange={(e) => setFormData({...formData, print_isbn: e.target.value})}
                      placeholder="978-0-123456-78-9"
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-600 mt-1">Leave blank - Amazon will provide a free ISBN</p>
                  </div>
                </div>

                {/* Age Range & Content Rating */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Age Range (if applicable)
                    </label>
                    <select
                      value={formData.age_range}
                      onChange={(e) => setFormData({...formData, age_range: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Not applicable</option>
                      <option value="0-2">0-2 years</option>
                      <option value="3-5">3-5 years</option>
                      <option value="6-8">6-8 years</option>
                      <option value="9-12">9-12 years</option>
                      <option value="13-17">13-17 years (Young Adult)</option>
                      <option value="18+">18+ (Adult)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Content Rating *
                    </label>
                    <select
                      required
                      value={formData.content_rating}
                      onChange={(e) => setFormData({...formData, content_rating: e.target.value})}
                      className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="general">General Audience</option>
                      <option value="teen">Teen (13+)</option>
                      <option value="mature">Mature (17+)</option>
                      <option value="adult">Adult Content (18+)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Reason/Purpose */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                What do you want to do with this work? *
              </label>
              <select
                required
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select one...</option>
                {serviceType === 'copyright' ? (
                  <>
                    <option value="protect_before_sharing">Protect before sharing with anyone</option>
                    <option value="protect_before_pitching">Protect before pitching to producers/agents</option>
                    <option value="protect_before_publishing">Protect before publishing</option>
                    <option value="proof_of_ownership">Establish proof of ownership</option>
                    <option value="other">Other (explain in notes)</option>
                  </>
                ) : (
                  <>
                    <option value="publish_first_book">Publish my first book on Amazon</option>
                    <option value="publish_another_book">Publish another book (I have published before)</option>
                    <option value="reformat_existing">Reformat existing book</option>
                    <option value="wide_distribution">Publish to Amazon + other platforms</option>
                    <option value="other">Other (explain in notes)</option>
                  </>
                )}
              </select>
            </div>

            {/* Additional Files (Optional) */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                Additional Files (Optional)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <input
                  type="file"
                  onChange={(e) => setFormData({...formData, additional_files: e.target.files[0]})}
                  className="hidden"
                  id="file-upload"
                  accept=".pdf,.docx,.doc"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="text-purple-600 hover:text-purple-700 font-semibold">
                    Click to upload
                  </span>
                  <span className="text-gray-600"> or drag and drop</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {serviceType === 'copyright' 
                    ? 'Cover image, synopsis, or other materials'
                    : 'Cover design, author bio, marketing materials'}
                </p>
                {formData.additional_files && (
                  <div className="mt-2 text-sm text-green-600 font-semibold">
                    ✓ {formData.additional_files.name}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                Additional Notes or Questions
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={4}
                placeholder={serviceType === 'copyright' 
                  ? 'Any specific requirements or concerns about registration?'
                  : 'Book genre, target audience, cover preferences, or any questions?'}
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>

            {/* Contact Preference */}
            <div>
              <label className="block text-sm font-semibold mb-2">
                How should we contact you?
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="email"
                    checked={formData.contact_preference === 'email'}
                    onChange={(e) => setFormData({...formData, contact_preference: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span>Email (fastest response)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="dashboard"
                    checked={formData.contact_preference === 'dashboard'}
                    onChange={(e) => setFormData({...formData, contact_preference: e.target.value})}
                    className="w-4 h-4"
                  />
                  <span>Dashboard notification</span>
                </label>
              </div>
            </div>

            {/* Error */}
            {error && (
              <Alert className="bg-red-50 border-red-300">
                <AlertDescription className="text-red-800">
                  {error}
                </AlertDescription>
              </Alert>
            )}

            {/* Payment Required Notice */}
            {!hasPaid && !paymentChecking && (
              <Alert className="bg-blue-50 border-blue-300">
                <CreditCard className="w-4 h-4" />
                <AlertDescription className="text-blue-800">
                  Payment of <strong>{info.price}</strong> is required before submitting your service request. 
                  Click "Proceed to Payment" to complete your purchase securely via Stripe.
                </AlertDescription>
              </Alert>
            )}

            {/* Submit / Payment Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={submitting || paymentChecking}
                className="flex-1"
              >
                Cancel
              </Button>
              
              {paymentChecking ? (
                <Button disabled className="flex-1 bg-purple-600">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </Button>
              ) : !hasPaid ? (
                <Button
                  type="button"
                  onClick={handleProceedToPayment}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Proceed to Payment ({info.price})
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {serviceType === 'copyright' 
                        ? 'Submit for Copyright' 
                        : 'Submit for Publishing'}
                    </>
                  )}
                </Button>
              )}
            </div>

            <p className="text-xs text-center text-gray-500">
              {hasPaid 
                ? 'Payment confirmed. Submit your service request to proceed.'
                : 'Secure payment powered by Stripe. You will be redirected to complete your purchase.'}
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
